/**
 * Part 11h: the New book window and Book details, one window (cockatrice-ui `pages/books/NewBook.jsx`, table BW in the
 * part 11h log). New book: Start blank (the cover, title, subtitle, author, language and word goal; Create book) or
 * Import a file (a Word or Markdown file, or a bundle, handed to their imports). Book details: the open book's same
 * fields, kept in the window until Save changes; Cancel, Close, Escape and a click beside the window keep the book as it
 * was (BW8, BW9).
 *
 * The cover is a colour from the six, or a photo through part 16a's pipeline at the cover's size (decision 37), stored
 * among the book's images and named by its details' `cover` (M17).
 */
import { createSignal, mergeProps, onCleanup } from 'solid-js';
import type { Accessor } from 'solid-js';
import { BOOK_LANGUAGES, COVER_COLOURS, METADATA_TEXT, normalizeText } from '@cockatrice/layout';
import type { BookLanguage, BookMetadata, CoverColour } from '@cockatrice/layout';
import type { ImageReference } from '@cockatrice/core';
import type { ImportResult } from '../images/importer.ts';
import { IMPORT_ACCEPT } from '../import/kind.ts';
import { LOCKED_ENDING } from '../backup/lock.ts';
import { MAX_GOAL } from '../shell/settings.ts';
import type { BookSummary } from '../shell/workspace.ts';
import { focusOn } from './announce.ts';
import { colourAt, coverColourOf, coverIndex, coverPhotoUrl } from './cover.ts';
import { el, when, whenSome } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import { continueWriting } from './home.ts';
import { pickBundle } from './locked-import.ts';
import { ModalActions, ModalBody, ModalButtons, ModalFoot, ModalHead, ModalNote } from './modal.ts';
import Button from '../ui/Button.ts';
import Card from '../ui/Card.ts';
import UiCover from '../ui/Cover.ts';
import FileDrop from '../ui/FileDrop.ts';
import UiIcon from '../ui/Icon.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Select from '../ui/Select.ts';
import Tabs from '../ui/Tabs.ts';
import TextField from '../ui/TextField.ts';
import type { TextFieldProps } from '../ui/TextField.ts';
import { named } from '../ui/Field.ts';
import { component } from '../ui/solid.ts';
import { languageName } from './settings-view.ts';
import type { Shell } from './shell.ts';

/** The photo the window holds: none, the book's stored one (BW10), or one picked and not yet added (BW6). */
type Photo = { readonly kind: 'none' } | { readonly kind: 'kept'; readonly reference: ImageReference } | { readonly kind: 'picked'; readonly file: File; readonly url: string };

/** The photo types the image pipeline takes (part 16a's header check). */
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

/** What the window edits, as it opens. */
interface Start {
  readonly title: string;
  readonly subtitle: string;
  readonly author: string;
  readonly language: BookLanguage;
  readonly colour: CoverColour;
  readonly photo: Photo;
  readonly goal: number | null;
}

/** BW2: a new book's start: no title, the author and language of the book written in last, the next colour of the six. */
function newStart(shell: Shell): Start {
  const books = shell.workspace.books();
  const readable = books.flatMap((book) => (book.metadata === null ? [] : [book.metadata]));
  return {
    title: '',
    subtitle: '',
    author: readable.findLast((metadata) => metadata.author !== '')?.author ?? '',
    language: readable.at(-1)?.language ?? 'en-US',
    colour: colourAt(books.length),
    photo: { kind: 'none' },
    goal: null,
  };
}

/** BW8: the open book's details as they are. */
function bookStart(shell: Shell, book: BookSummary, metadata: BookMetadata): Start {
  return {
    title: metadata.title,
    subtitle: metadata.subtitle,
    author: metadata.author,
    language: metadata.language,
    colour: coverColourOf(book.id, metadata),
    photo: metadata.cover === null ? { kind: 'none' } : { kind: 'kept', reference: metadata.cover },
    goal: shell.workspace.writing(book.id).bookGoal,
  };
}

/** BW4: a word goal as typed: blank is none; a whole number from 1 to {@link MAX_GOAL}; anything else is refused. */
function readGoal(typed: string): number | null | 'refused' {
  const trimmed = typed.trim().replace(/[\s,.]/gu, '');
  if (trimmed === '') return null;
  if (!/^\d+$/u.test(trimmed)) return 'refused';
  const goal = Number(trimmed);
  return goal >= 1 && goal <= MAX_GOAL ? goal : 'refused';
}

/** What stopped a photo, in words (part 16a's refusals, the book's image cap, a failure). */
export function photoError(shell: Pick<Shell, 't'>, result: ImportResult): string {
  const { t } = shell;
  if (result.kind === 'refused') return t(`cover.refused.${result.reason}`);
  return t(result.kind === 'full' ? 'cover.refused.full' : 'cover.refused.failed');
}

/** The book's stored photo the window holds, else null. */
function keptOf(photo: Photo): ImageReference | null {
  return photo.kind === 'kept' ? photo.reference : null;
}

/** The photo's reference once added (or found among the book's images), else null. */
function referenceOf(result: ImportResult): ImageReference | null {
  return result.kind === 'added' || result.kind === 'existing' ? result.reference : null;
}

/** A one-line text field (the library's TextField) with its label and help, starting at `value`, at most `max` long. */
function Text(id: string, label: string, value: string, set: (value: string) => void, max: number, extra: TextFieldProps = {}): Child {
  // Merged, not spread: the goal's error is a getter, read as it changes.
  return component(
    TextField,
    mergeProps(
      {
        id,
        label,
        defaultValue: value,
        maxLength: max,
        onChange: (typed: string) => {
          set(typed);
        },
      },
      named(extra),
    ),
  );
}

/** Everything the window keeps while it is open. */
interface Draft {
  readonly title: Accessor<string>;
  readonly setTitle: (value: string) => void;
  readonly subtitle: Accessor<string>;
  readonly setSubtitle: (value: string) => void;
  readonly author: Accessor<string>;
  readonly setAuthor: (value: string) => void;
  readonly language: Accessor<BookLanguage>;
  readonly setLanguage: (value: BookLanguage) => void;
  readonly goal: Accessor<string>;
  readonly setGoal: (value: string) => void;
  readonly colour: Accessor<CoverColour>;
  readonly setColour: (value: CoverColour) => void;
  readonly kind: Accessor<'colour' | 'photo'>;
  readonly setKind: (value: 'colour' | 'photo') => void;
  readonly photo: Accessor<Photo>;
  readonly pick: (file: File) => void;
  readonly drop: () => void;
}

/** The window's draft from `start`; a picked photo's object URL is let go as another replaces it and as the window goes. */
function draftOf(shell: Shell, start: Start, failed: () => void): Draft {
  const [title, setTitle] = createSignal(start.title);
  const [subtitle, setSubtitle] = createSignal(start.subtitle);
  const [author, setAuthor] = createSignal(start.author);
  const [language, setLanguage] = createSignal<BookLanguage>(start.language);
  const [goal, setGoal] = createSignal(start.goal === null ? '' : String(start.goal));
  const [colour, setColour] = createSignal<CoverColour>(start.colour);
  const [kind, setKind] = createSignal<'colour' | 'photo'>(start.photo.kind === 'none' ? 'colour' : 'photo');
  const [photo, setPhoto] = createSignal<Photo>(start.photo);
  const letGo = (): void => {
    const now = photo();
    if (now.kind === 'picked') shell.revokeUrl(now.url);
  };
  onCleanup(letGo);
  let reading = 0;
  return {
    title,
    setTitle,
    subtitle,
    setSubtitle,
    author,
    setAuthor,
    language,
    setLanguage,
    goal,
    setGoal,
    colour,
    setColour,
    kind,
    setKind,
    photo,
    pick: (file) => {
      // The latest pick wins, whichever read ends first.
      reading += 1;
      const mine = reading;
      void file.arrayBuffer().then(
        (bytes) => {
          if (mine !== reading) return;
          letGo();
          setPhoto({ kind: 'picked', file, url: shell.fileUrl(new Uint8Array(bytes), file.type === '' ? 'image/jpeg' : file.type) });
        },
        // BW6: a file that can't be read is said; the photo stays as it was.
        () => {
          if (mine === reading) failed();
        },
      );
    },
    drop: () => {
      reading += 1;
      letGo();
      setPhoto({ kind: 'none' });
    },
  };
}

/** A colour's look for the library's Cover: its place among the six (`data-cover`) and its tokens. */
function colourLook(colour: CoverColour): { readonly 'data-cover': string; readonly bg: string; readonly ink: string } {
  const index = coverIndex(colour);
  return { 'data-cover': String(index), bg: `var(--cover-${String(index)})`, ink: `var(--cover-${String(index)}-ink)` };
}

/** BW10: the cover drawn from the colour, the subtitle, the title (or "Untitled book") and the author, as the shelf will draw it. */
function ColourCover(shell: Shell, draft: Draft): Child {
  return component(UiCover, {
    kind: 'colour',
    class: 'bw-cover',
    decorative: true,
    get 'data-cover'() {
      return colourLook(draft.colour())['data-cover'];
    },
    get bg() {
      return colourLook(draft.colour()).bg;
    },
    get ink() {
      return colourLook(draft.colour()).ink;
    },
    get kicker() {
      return draft.subtitle();
    },
    get title() {
      return draft.title().trim() === '' ? shell.t('book.untitled') : draft.title();
    },
    get author() {
      return draft.author();
    },
  });
}

/** A hidden file input `open` clicks, its file handed to `chosen`. */
function Picker(accept: string, chosen: (file: File) => void, set: (input: HTMLInputElement) => void, className: string): Child {
  return el('input', {
    type: 'file',
    class: className,
    accept,
    hidden: true,
    tabIndex: -1,
    'aria-hidden': 'true',
    ref: set,
    onChange: (event: On<Event, HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0];
      // The same file chosen again must still fire a change.
      event.currentTarget.value = '';
      if (file !== undefined) chosen(file);
    },
  });
}

/** Handlers that take a file dropped on an element (its first): a photo dropped on the cover replaces it. */
function dropTarget(chosen: (file: File) => void): Readonly<Record<string, unknown>> {
  return {
    onDragOver: (event: DragEvent) => {
      event.preventDefault();
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault();
      const file = event.dataTransfer?.files[0];
      if (file !== undefined) chosen(file);
    },
  };
}

/**
 * The library's FileDrop handing on the file chosen or dropped: one that doesn't match `accept` too, as before, so its
 * own import or the photo's pipeline says why it can't be taken.
 */
function Drop(props: { readonly class: string; readonly hint: string; readonly buttonLabel: string; readonly icon: 'upload' | 'picture'; readonly accept: string; readonly chosen: (file: File) => void }, extra: Readonly<Record<string, unknown>> = {}): Child {
  const take = (files: File[]): void => {
    for (const file of files.slice(0, 1)) props.chosen(file);
  };
  return component(FileDrop, { class: props.class, hint: props.hint, buttonLabel: props.buttonLabel, icon: props.icon, accept: props.accept, ...extra, showFiles: false, onFiles: take, onReject: take });
}

/** The photo's cover, from the picked file or the book's stored photo, its title and author over it as on the shelf. */
function PhotoCover(shell: Shell, bookId: string, draft: Draft, photo: Exclude<Photo, { readonly kind: 'none' }>): Child {
  const src = photo.kind === 'picked' ? (): string => photo.url : coverPhotoUrl(shell, () => bookId, () => photo.reference);
  return component(UiCover, {
    kind: 'photo',
    class: 'bw-cover bw-cover--photo',
    alt: shell.t('cover.photo'),
    overlayTitle: true,
    get src() {
      return src() ?? undefined;
    },
    get title() {
      return draft.title().trim() === '' ? shell.t('book.untitled') : draft.title();
    },
    get author() {
      return draft.author();
    },
    ...dropTarget(draft.pick),
  });
}

/** BW6, BW10: the cover column: Colour or Photo; the six colours, or the photo with Replace… and Remove. */
function CoverColumn(shell: Shell, bookId: string, draft: Draft): Child {
  const { t } = shell;
  let picker: HTMLInputElement | undefined;
  const open = (): void => picker?.click();
  return el(
    'div',
    { class: 'bw-cover-col' },
    component(SegmentedControl, {
      label: t('cover.type'),
      class: 'bw-cover-type',
      options: [
        { value: 'colour', label: t('cover.colourTab') },
        { value: 'photo', label: t('cover.photoTab') },
      ],
      get value() {
        return draft.kind();
      },
      onChange: (value: string) => {
        draft.setKind(value === 'photo' ? 'photo' : 'colour');
      },
    }),
    Picker(PHOTO_ACCEPT, draft.pick, (input) => (picker = input), 'cover-picker'),
    when(
      () => draft.kind() === 'colour',
      () => [
        ColourCover(shell, draft),
        // BW15: the swatches stay the app's radio group of buttons (the library's RadioGroup is native radios with
        // labels, not a row of colours): one Tab stop, the colour chosen; the arrows move the choice.
        el(
          'div',
          { class: 'bw-swatches', role: 'radiogroup', 'aria-label': t('cover.colours') },
          ...COVER_COLOURS.map((colour, index) =>
            el('button', {
              type: 'button',
              role: 'radio',
              class: 'bw-swatch',
              'data-cover': String(index),
              'aria-checked': () => (draft.colour() === colour ? 'true' : 'false'),
              'aria-label': t(`cover.colour.${colour}`),
              // One stop of the Tab key, the colour chosen; the arrows move the choice (a native radio group's keys).
              tabIndex: () => (draft.colour() === colour ? 0 : -1),
              onClick: () => {
                draft.setColour(colour);
              },
              onKeyDown: (event: On<KeyboardEvent, HTMLButtonElement>) => {
                const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
                if (step === undefined) return;
                event.preventDefault();
                const next = colourAt(index + step + COVER_COLOURS.length);
                draft.setColour(next);
                const siblings = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button');
                siblings?.[COVER_COLOURS.indexOf(next)]?.focus();
              },
            }),
          ),
        ),
      ],
      // The photo part follows the photo as it is picked, replaced or removed: the drop zone while there is none, else
      // the photo (made again as it changes) with Replace… and Remove.
      () =>
        whenSome(
          () => {
            const photo = draft.photo();
            return photo.kind === 'none' ? undefined : photo;
          },
          (photo) => [
            () => PhotoCover(shell, bookId, draft, photo()),
            el(
              'div',
              { class: 'bw-inline-links' },
              component(Button, { variant: 'link', class: 'cover-replace', onClick: open, children: t('cover.replace') }),
              component(Button, {
                variant: 'link',
                class: 'cover-remove',
                onClick: () => {
                  draft.drop();
                },
                children: t('cover.remove'),
              }),
            ),
          ],
          () =>
            Drop({
              class: 'bw-photo-drop',
              hint: t('cover.photoHint'),
              buttonLabel: t('cover.upload'),
              icon: 'picture',
              accept: PHOTO_ACCEPT,
              chosen: draft.pick,
            }),
        ),
    ),
  );
}

/** BW3, BW4: the fields: title, subtitle, author, and the language beside the word goal (the library's fields). */
function Fields(shell: Shell, draft: Draft, goalError: Accessor<string>): Child {
  const { t, locale } = shell;
  const names = new Intl.DisplayNames(locale, { type: 'language' });
  return el(
    'div',
    { class: 'bw-fields' },
    Text('bw-title', t('newBook.bookTitle'), draft.title(), draft.setTitle, METADATA_TEXT.title.max, { placeholder: t('book.untitled'), autofocus: true }),
    Text('bw-subtitle', t('newBook.subtitle'), draft.subtitle(), draft.setSubtitle, METADATA_TEXT.subtitle.max, { optional: true }),
    Text('bw-author', t('newBook.author'), draft.author(), draft.setAuthor, METADATA_TEXT.author.max, { help: t('newBook.authorHint'), autocomplete: 'name' }),
    el(
      'div',
      { class: 'bw-field-row' },
      component(Select, {
        id: 'bw-language',
        label: t('newBook.language'),
        help: t('newBook.languageHint'),
        fieldClass: 'bw-field',
        fullWidth: true,
        defaultValue: draft.language(),
        options: BOOK_LANGUAGES.map((tag) => ({ value: tag, label: languageName(names, tag) })),
        onChange: (value: string) => {
          const chosen = BOOK_LANGUAGES.find((tag) => tag === value);
          if (chosen !== undefined) draft.setLanguage(chosen);
        },
      }),
      Text('bw-goal', t('newBook.goal'), draft.goal(), draft.setGoal, 12, {
        fieldClass: 'bw-field',
        optional: true,
        inputMode: 'numeric',
        placeholder: t('newBook.goalPlaceholder'),
        get error() {
          return goalError() === '' ? undefined : goalError();
        },
      }),
    ),
  );
}

/** BW1: the import tab: the file dropped or chosen, then Import hands it to the Word/Markdown import or the bundle's. */
function ImportTab(shell: Shell, chosen: Accessor<File | null>, choose: (file: File) => void): Child {
  const { t } = shell;
  return el(
    'div',
    { class: 'bw-import' },
    Drop(
      {
        class: 'bw-drop',
        hint: t('newBook.dropHint'),
        buttonLabel: t('newBook.choose'),
        icon: 'upload',
        accept: `${IMPORT_ACCEPT},.zip,application/zip,${LOCKED_ENDING}`,
        chosen: choose,
      },
      {
        id: 'bw-import-picker',
        title: t('newBook.drop'),
        children: el('p', { class: 'bw-file', role: 'status' }, () => {
          const file = chosen();
          return file === null ? '' : t('newBook.chosen', { name: file.name });
        }),
      },
    ),
    el(
      'div',
      { class: 'bw-import__notes' },
      component(Card, { padding: 'sm', title: t('newBook.wordNote'), children: t('newBook.wordNoteText') }),
      component(Card, { padding: 'sm', title: t('newBook.bundleNote'), children: t('newBook.bundleNoteText') }),
    ),
  );
}

/** Whether `file` is a bundle (a ZIP, or a locked backup), which goes to the bundle's import. */
function isBundle(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.zip') || name.endsWith(LOCKED_ENDING) || file.type === 'application/zip';
}

/** BW1: hands `file` to its import, the window closed and the focus on the import's Cancel (A24). */
function startImport(shell: Shell, file: File): void {
  shell.ui.setDialog(null);
  if (isBundle(file)) {
    void pickBundle(shell, file);
    return;
  }
  shell.importer.pick(file);
  focusOn(shell, '.cancel-import');
}

/** The details the draft gives, written over `metadata`, each text made storable (table N) and kept within its length. */
function detailsFrom(draft: Draft, metadata: BookMetadata, cover: ImageReference | null, unpicked: CoverColour | null): BookMetadata {
  const text = (value: string, key: 'title' | 'subtitle' | 'author'): string => normalizeText(value, METADATA_TEXT[key]).trim().slice(0, METADATA_TEXT[key].max);
  // BW10: a colour left as the ID gave it (`unpicked`, for a book with none picked) stays unpicked.
  const colour = draft.colour() === unpicked ? null : draft.colour();
  return { ...metadata, title: text(draft.title(), 'title'), subtitle: text(draft.subtitle(), 'subtitle'), author: text(draft.author(), 'author'), language: draft.language(), coverColour: colour, cover };
}

/** The book Book details edits: the open one, readable (B3). */
interface Edited {
  readonly book: BookSummary;
  readonly metadata: BookMetadata;
}

/**
 * The window: New book, or Book details of the open book (`error` what stopped the photo of the book just made, BW7).
 * BW13: a book whose record can't be read is never overwritten (B3): the window says so and offers nothing to save.
 */
export function BookWindow(shell: Shell, mode: 'new' | 'edit', error = ''): Child {
  const { t, ui, workspace } = shell;
  if (mode === 'new') return Window(shell, null, error);
  const edited = readable(workspace.book());
  if (edited === null) {
    return [ModalHead(shell, t('details.title')), ModalBody({}, el('p', {}, t('book.unreadable'))), ModalButtons(component(Button, { variant: 'secondary', onClick: () => ui.setDialog(null), children: t('newBook.cancel') }))];
  }
  return Window(shell, edited, error);
}

/** The open book with its details, while there is one and they can be read. */
function readable(book: BookSummary | null): Edited | null {
  if (book === null) return null;
  const { metadata } = book;
  return metadata === null ? null : { book, metadata };
}

/** Book details' line: the book's words where known, its chapters, the day it was made. */
function summaryOf(shell: Shell, book: BookSummary): string {
  const { t, workspace } = shell;
  const words = workspace.writing(book.id).words;
  const chapters = workspace.listing()?.chapters.length ?? null;
  const parts = [
    words === null ? null : t('home.words', { count: words }),
    chapters === null ? null : t('sharing.chapterCount', { count: chapters }),
    book.createdAt === null ? null : t('details.created', { date: new Intl.DateTimeFormat(shell.locale, { dateStyle: 'long' }).format(book.createdAt) }),
  ];
  return parts.filter((part) => part !== null).join(' · ');
}

/** The window over `edited` (Book details), or over nothing (New book). */
function Window(shell: Shell, edited: Edited | null, error: string): Child {
  const { t, ui, workspace } = shell;
  const editing = edited !== null;
  const start = edited === null ? newStart(shell) : bookStart(shell, edited.book, edited.metadata);
  const [problem, setProblem] = createSignal(error);
  const draft = draftOf(shell, start, () => setProblem(t('cover.refused.failed')));
  const [tab, setTab] = createSignal<'blank' | 'import'>('blank');
  const [chosen, setChosen] = createSignal<File | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [goalError, setGoalError] = createSignal('');

  const goalOf = (): number | null | 'refused' => {
    const goal = readGoal(draft.goal());
    setGoalError(goal === 'refused' ? t('newBook.goalRefused', { max: MAX_GOAL.toLocaleString(shell.locale) }) : '');
    return goal;
  };

  /** BW8: Save changes: the photo added first (the window stays, saying why, if it can't be), then the details in one write. */
  const save = async ({ book, metadata }: Edited): Promise<void> => {
    const goal = goalOf();
    if (goal === 'refused') {
      focusOn(shell, '#bw-goal');
      return;
    }
    setBusy(true);
    setProblem('');
    const photo = draft.photo();
    // BW10: Colour chosen drops any photo; Photo keeps the stored one, or adds the one picked.
    let cover = draft.kind() === 'colour' ? null : keptOf(photo);
    if (draft.kind() === 'photo' && photo.kind === 'picked') {
      const added = await shell.addCover(book.id, photo.file);
      cover = referenceOf(added);
      if (cover === null) {
        setBusy(false);
        setProblem(photoError(shell, added));
        return;
      }
    }
    await workspace.changeDetails((current) => detailsFrom(draft, current, cover, metadata.coverColour === null ? start.colour : null));
    if (goal !== workspace.writing(book.id).bookGoal) await workspace.changeWriting(book.id, { bookGoal: goal });
    setBusy(false);
    ui.setDialog(null);
  };

  /** BW5–BW7: Create book: the book made and opened in the writer, its goal set, then its photo added (BW7 if it can't be). */
  const create = async (): Promise<void> => {
    const goal = goalOf();
    if (goal === 'refused') {
      focusOn(shell, '#bw-goal');
      return;
    }
    setBusy(true);
    const photo = draft.photo();
    const id = await workspace.createBook((fresh) => detailsFrom(draft, fresh, null, null));
    if (id === null) {
      setBusy(false);
      ui.setDialog(null);
      return;
    }
    if (goal !== null) await workspace.changeWriting(id, { bookGoal: goal });
    ui.setDialog(null);
    continueWriting(shell);
    if (draft.kind() !== 'photo' || photo.kind !== 'picked') return;
    const added = await shell.addCover(id, photo.file);
    const reference = referenceOf(added);
    // The book made is the open one, unless the writer has gone on to another meanwhile.
    if (workspace.book()?.id !== id) return;
    if (reference !== null) await workspace.changeDetails((current) => ({ ...current, cover: reference }));
    else ui.setDialog({ kind: 'book', error: t('cover.madeWithout', { reason: photoError(shell, added) }) });
  };

  const submit = (event: SubmitEvent): void => {
    event.preventDefault();
    if (busy() || !workspace.writable()) return;
    if (!editing && tab() === 'import') {
      const file = chosen();
      if (file !== null) startImport(shell, file);
      return;
    }
    void (edited === null ? create() : save(edited));
  };

  return [
    ModalHead(shell, edited === null ? t('newBook.title') : t('details.title'), edited === null ? {} : { subtitle: summaryOf(shell, edited.book) }),
    // The library's Tabs (their arrow keys, Home and End); the panel is the window's body, inside its form.
    editing
      ? null
      : component(Tabs, {
          id: 'bw',
          class: 'bw-tabs',
          label: t('newBook.tabs'),
          panelId: 'bw-panel',
          tabs: [
            { value: 'blank', label: t('newBook.blank') },
            { value: 'import', label: t('newBook.import') },
          ],
          get value() {
            return tab();
          },
          onChange: (value: string) => setTab(value === 'import' ? 'import' : 'blank'),
        }),
    el(
      'form',
      { class: 'c-modal__form', noValidate: true, onSubmit: submit },
      ModalBody(
        { id: 'bw-panel', ...(editing ? {} : { role: 'tabpanel', 'aria-labelledby': () => `bw-tab-${tab()}` }) },
        el('p', { class: 'ac-error bw-problem', role: 'alert' }, problem),
        when(
          () => editing || tab() === 'blank',
          () => el('div', { class: 'bw-book' }, CoverColumn(shell, edited?.book.id ?? '', draft), Fields(shell, draft, goalError)),
          () => ImportTab(shell, chosen, setChosen),
        ),
      ),
      ModalFoot(
        {},
        editing
          ? el(
              'div',
              { class: 'bw-foot-links' },
              component(Button, {
                variant: 'link',
                class: 'bw-export-bundle',
                onClick: () => {
                  ui.setDialog(null);
                  shell.bundler.start();
                  focusOn(shell, '.cancel-bundle');
                },
                children: t('details.exportBundle'),
              }),
            )
          : ModalNote(component(UiIcon, { name: 'lock', size: 'sm' }), t('newBook.private')),
        ModalActions(
          component(Button, { variant: 'secondary', class: 'bw-cancel', onClick: () => ui.setDialog(null), children: t('newBook.cancel') }),
          component(Button, {
            variant: 'primary',
            type: 'submit',
            class: 'bw-submit',
            get disabled() {
              return busy() || !workspace.writable() || (!editing && tab() === 'import' && chosen() === null);
            },
            get children() {
              return editing ? t('details.save') : tab() === 'import' ? t('newBook.importAction') : t('newBook.create');
            },
          }),
        ),
      ),
    ),
  ];
}

